/* eslint-disable no-nested-ternary,max-len,quote-props,lines-between-class-members */
import fromPairs from 'lodash/fromPairs';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React from 'react';
import ReactDOM from 'react-dom';
import { mapboxAccessToken } from '../../config';
import CollectionPointPopup from './components/CollectionPointPopup';

mapboxgl.accessToken = mapboxAccessToken;

export const POINT_MARKER_HEIGHT = 64;
export const POINT_MARKER_WIDTH = 58;
export const POINT_MARKER_RADIUS = POINT_MARKER_WIDTH / 2;
export const COLOR_CIRCLE_RADIUS = 20;
export const POINT_MARKER_Y_OFFSET = -29;

export const SOURCE_ID = 'wasteBinsSource';
export const LAYER_ID = 'wasteBinsLayer';

export default class Map {
  // for perfomance, keep every created Marker for cluster or point and reuse it instead of creating new all the time
  allCachedClusterMarkers = {};
  clusterMarkersCurrentlyOnScreen = {};

  allCachedPointMarkers = {};
  pointMarkersCurrentlyOnScreen = {};

  filters = [];
  data = [];
  mapWasteBins = {};

  constructor(containerId, filtersOpen, isMobile) {
    this.filtersOpen = filtersOpen;
    this.isMobile = isMobile;
    this.map = new mapboxgl.Map({
      container: containerId,
      style: 'mapbox://styles/sisms/cjubgsh4105s51fmhdtb92ikh',
      center: [19, 52],
      zoom: 5,
    })
      .addControl(new mapboxgl.FullscreenControl(), 'top-right')
      .addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right')
      .addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true }, trackUserLocation: false,
        }),
        'bottom-right');

    this.map.on('load', () => {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: geoJsonForCollectionPoints(this.data, this.filters),
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 60,
        clusterProperties: fromPairs(
          Object.values(this.mapWasteBins).map(wasteBin =>
            [wasteBin.code, ['+', ['case', ['has', wasteBin.code, ['get', 'wasteBinsCodesOb']], 1, 0]]]),
        ),
      });
      //we need a layer for querySourceFeatures() to work - not sure why
      this.map.addLayer({
        id: LAYER_ID,
        source: SOURCE_ID,
        type: 'symbol',
      });
      // after the GeoJSON data is loaded, update markers on the screen and do so on every map move/moveend
      this.map.on('data', () => {
        this.map.on('move', () => {
          this.updateMarkers();
        });
        this.map.on('moveend', () => {
          this.updateMarkers();
        });
        this.updateMarkers();
      });
    });
  }

  updateMarkers() {
    const featuresCurrentlyVisibleOnMap = this.map.querySourceFeatures(SOURCE_ID);
    this.updateClusterMarkers(featuresCurrentlyVisibleOnMap);
    this.updatePointMarkers(featuresCurrentlyVisibleOnMap);
  }

  updateClusterMarkers(featuresCurrentlyVisibleOnMap) {
    const newMarkers = {};

    // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
    // and add it to the map if it's not there already
    featuresCurrentlyVisibleOnMap
      .filter(feature => feature.properties.cluster)
      .forEach((feature) => {
        const { properties, geometry: { coordinates } } = feature;
        const clusterId = properties.cluster_id;

        let marker = this.allCachedClusterMarkers[clusterId];
        if (!marker) {
          const element = createClusterSvg(properties, this.mapWasteBins);
          element.addEventListener('click', () => {
            this.map.getSource(SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (!err) {
                this.map.easeTo({
                  center: coordinates,
                  zoom,
                });
              }
            });
          });
          const newMarker = new mapboxgl.Marker({ element }).setLngLat(coordinates);
          marker = newMarker;
          this.allCachedClusterMarkers[clusterId] = newMarker;
        }
        newMarkers[clusterId] = marker;

        if (!this.clusterMarkersCurrentlyOnScreen[clusterId]) {
          marker.addTo(this.map);
        }
      });
    // for every marker we've added previously, remove those that are no longer visible
    Object.entries(this.clusterMarkersCurrentlyOnScreen).forEach(([clusterId, marker]) => {
      if (!newMarkers[clusterId]) {
        marker.remove();
      }
    });
    this.clusterMarkersCurrentlyOnScreen = newMarkers;
  }

  updatePointMarkers(featuresCurrentlyVisibleOnMap) {
    const newMarkers = {};

    // for every point on the screen, create an HTML marker for it (if we didn't yet),
    // and add it to the map if it's not there already
    featuresCurrentlyVisibleOnMap
      .filter(feature => !feature.properties.cluster)
      .forEach((feature) => {
        const { properties, geometry: { coordinates } } = feature;
        const { id } = properties;

        let marker = this.allCachedPointMarkers[id];
        if (!marker) {
          const element = createPointSvg(properties, this.mapWasteBins);
          // offset icon to make the pin hit the target coordinates
          const newMarker = new mapboxgl.Marker({ element, offset: [0, -64 / 2] }).setLngLat(coordinates);
          element.addEventListener('click', () => {
            const divNode = document.createElement('div');
            ReactDOM.render(<CollectionPointPopup {...properties} wasteBins={this.mapWasteBins} />, divNode);
            const popup = new mapboxgl.Popup({
              offset: getPopupOffsets(POINT_MARKER_HEIGHT, POINT_MARKER_RADIUS, POINT_MARKER_Y_OFFSET),
              className: 'collectionPointPopup',
              maxWidth: 'none',
            });
            popup.setDOMContent(divNode);
            newMarker.setPopup(popup);
          });
          marker = newMarker;
          this.allCachedPointMarkers[id] = newMarker;
        }
        newMarkers[id] = marker;
        if (!this.pointMarkersCurrentlyOnScreen[id]) {
          marker.addTo(this.map);
        }
      });
    // for every marker we've added previously, remove those that are no longer visible
    Object.entries(this.pointMarkersCurrentlyOnScreen).forEach(([pointId, marker]) => {
      if (!newMarkers[pointId]) {
        marker.remove();
      }
    });
    this.pointMarkersCurrentlyOnScreen = newMarkers;
  }

  resetCache() {
    removeAllMarkersInObject(this.allCachedClusterMarkers);
    removeAllMarkersInObject(this.clusterMarkersCurrentlyOnScreen);
    removeAllMarkersInObject(this.allCachedPointMarkers);
    removeAllMarkersInObject(this.pointMarkersCurrentlyOnScreen);
    this.allCachedClusterMarkers = {};
    this.clusterMarkersCurrentlyOnScreen = {};
    this.allCachedPointMarkers = {};
    this.pointMarkersCurrentlyOnScreen = {};
  }

  refreshSource() {
    const source = this.map.getSource(SOURCE_ID);
    if (source) {
      source.setData(geoJsonForCollectionPoints(this.data, this.filters));
      this.resetCache();
      this.updateMarkers();
    }
  }

  fitMapToPoints(filteredDataCoordinates) {
    const { min, max } = getLngLatCorners(filteredDataCoordinates);
    const padding = this.isMobile ? 40 : 100;
    const desktopFiltersWidth = this.isMobile ? 0 : this.filtersOpen ? 300 : 110;
    this.map.fitBounds([min, max], {
      maxZoom: 16,
      padding: {
        top: padding,
        bottom: padding,
        right: padding,
        left: padding + desktopFiltersWidth,
      },
    });
  }

  centerMapOnDataPointsCenter() {
    const selectedFiltersSet = new Set(this.filters);
    const filteredData = this.data.filter(point => point.types.filter(type => selectedFiltersSet.has(type)).length > 0);
    if (filteredData.length > 0) {
      const filteredDataCoordinates = filteredData.map(point => [Number(point.longitude), Number(point.latitude)]);
      this.fitMapToPoints(filteredDataCoordinates);
    } else if (this.data.length > 0) {
      // jesli są jakieś punkty, ale wszystkie są odznaczone to mimo wszystko lepiej jest wycentrować mapę
      // na rejon, gdzie są wszystkie punkty niż pozostać oddalonym na całą Polskę
      const allPointsCoordinates = this.data.map(point => [Number(point.longitude), Number(point.latitude)]);
      this.fitMapToPoints(allPointsCoordinates);
    }
  }

  /** for setting filters and reflecting it in points on map */
  setFilters(newFilters) {
    this.filters = newFilters;
    this.refreshSource();
    this.centerMapOnDataPointsCenter();
  }

  setFiltersOpen(newFiltersOpen) {
    this.filtersOpen = newFiltersOpen;
  }

  setIsMobile(isMobile) {
    this.isMobile = isMobile;
  }

  /** for setting data of points on map */
  setData(newData) {
    this.data = newData;
    this.refreshSource();
    this.centerMapOnDataPointsCenter();
  }

  setMapWasteBins(newMapWasteBins) {
    this.mapWasteBins = newMapWasteBins;
    this.refreshSource();
  }

  remove() {
    this.map.remove();
    this.map = undefined;
  }
}

function geoJsonForCollectionPoints(data, filters) {
  const filteredCollectionPoints = data.filter((collectionPoint) => {
    const filtersSet = new Set(filters);
    return collectionPoint.types.some(d => filtersSet.has(d));
  });
  return {
    type: 'FeatureCollection',
    features: filteredCollectionPoints.map(({ latitude, longitude, types, ...properties }) => {
      // ommit properties which are explicitly null, because they end up as string "null" when retriving properties via querySourceFeatures
      const cleanedProperties = fromPairs(Object.entries(properties).filter(entry => entry[1] !== null));
      return ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
        properties: {
          wasteBinsCodesOb: fromPairs(types.map(type => [type, type])), // has to be an object to allow summing in clusterProperties via 'has' expression
          ...cleanedProperties,
        },
      });
    }),
  };
}

function removeAllMarkersInObject(objectToClear) {
  Object.values(objectToClear).forEach((marker) => {
    marker.remove();
  });
}

function createClusterSvg(properties, mapWasteBins) {
  const colors = Object.values(mapWasteBins).map(wasteBin => wasteBin.color);
  const counts = Object.values(mapWasteBins).map(wasteBin => properties[wasteBin.code]);
  const { total, offsets } = counts.reduce(
    (acc, count) => ({ total: acc.total + count, offsets: [...acc.offsets, acc.total] }),
    { total: 0, offsets: [] },
  );
  const bigCount = 50;
  const mediumCount = 20;
  const small = 10;
  const fontSize = properties.point_count >= bigCount ? 19 : properties.point_count >= mediumCount ? 18 : properties.point_count >= small ? 15 : 14;
  const externalRadius = properties.point_count >= bigCount ? 50 : properties.point_count >= mediumCount ? 30 : properties.point_count >= small ? 26 : 24;
  const internalRadius = Math.round(externalRadius * 0.45);
  const width = externalRadius * 2;

  const svg = `<svg width="${width}" height="${width}" viewbox="0 0 ${width} ${width}" class="clusterMarker">
  ${counts.map((_, i) => donutSegment(
    offsets[i] / total,
    (offsets[i] + counts[i]) / total,
    externalRadius,
    internalRadius,
    colors[i],
  ))}
<circle cx="${externalRadius}" cy="${externalRadius}" r="${internalRadius}" fill="white" />
<text dominant-baseline="central" text-anchor="middle" style="font: ${fontSize}px sans-serif" transform="translate(${externalRadius}, ${externalRadius})">${properties.point_count}</text>
</svg>
`;

  const el = document.createElement('div');
  el.innerHTML = svg;
  return el.firstChild;
}

function donutSegment(start, providedEnd, r, r0, color) {
  const end = providedEnd - start === 1 ? providedEnd - 0.00001 : providedEnd;
  const a0 = 2 * Math.PI * (start - 0.25);
  const a1 = 2 * Math.PI * (end - 0.25);
  const x0 = Math.cos(a0);
  const y0 = Math.sin(a0);
  const x1 = Math.cos(a1);
  const y1 = Math.sin(a1);
  const largeArc = end - start > 0.5 ? 1 : 0;
  return `
<path d="
M ${r + r0 * x0} ${r + r0 * y0} 
L ${r + r * x0} ${r + r * y0} 
A ${r} ${r} ${0} ${largeArc} ${1} ${r + r * x1} ${r + r * y1} 
L ${r + r0 * x1} ${r + r0 * y1} 
A ${r0} ${r0} ${0} ${largeArc} ${0} ${r + r0 * x0} ${r + r0 * y0}
" fill="${color}" />
`;
}

function createPointSvg(properties, wasteBins) {
  const parsedWasteBinsCodesOb = JSON.parse(properties.wasteBinsCodesOb);
  const wasteBinsCodes = Object.values(parsedWasteBinsCodesOb);
  const wasteBinsColors = wasteBinsCodes.map(code => wasteBins[code].color);

  const colorCircleOffset = (POINT_MARKER_WIDTH - COLOR_CIRCLE_RADIUS * 2) / 2;
  const svg = `<svg width="${POINT_MARKER_WIDTH}" height="${POINT_MARKER_HEIGHT}" viewbox="0 0 58 64" class="pointMarker">
  <defs>
    <filter id="pinshadow" width="${POINT_MARKER_WIDTH}" height="${POINT_MARKER_HEIGHT}" x="0" y="0" filterUnits="userSpaceOnUse">
      <feOffset/>
      <feGaussianBlur result="blur" stdDeviation="2"/>
      <feFlood flood-opacity=".349"/>
      <feComposite in2="blur" operator="in"/>
      <feComposite in="SourceGraphic"/>
    </filter>
  </defs>
  <g filter="url(#pinshadow)">
    <path d="M21.2 50.642A23.011 23.011 0 0 1 6 29 23 23 0 0 1 29 6a23 23 0 0 1 23 23 23.01 23.01 0 0 1-15.195 21.642L29 58z" fill="#fff"/>
  </g>
  <g transform="translate(${colorCircleOffset}, ${colorCircleOffset})">
    ${wasteBinsCodes.length === 1
    ? `<foreignObject width="40" height="40">
        ${wasteBins[wasteBinsCodes[0]].iconRaw}
      </foreignObject>`
    : (
      wasteBinsColors.map((_, i) => donutSegment(
        i / wasteBinsColors.length,
        (i + 1) / wasteBinsColors.length,
        COLOR_CIRCLE_RADIUS,
        0,
        wasteBinsColors[i]))
    )}
  </g>
</svg>
`;

  const el = document.createElement('div');
  el.innerHTML = svg;
  return el.firstChild;
}

function getPopupOffsets(markerHeight, markerRadius, linearOffset) {
  return {
    'top': [0, 0],
    'top-left': [0, 0],
    'top-right': [0, 0],
    'bottom': [0, -markerHeight],
    'bottom-left': [linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
    'bottom-right': [-linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
    'left': [markerRadius, (markerHeight - markerRadius) * -1],
    'right': [-markerRadius, (markerHeight - markerRadius) * -1],
  };
}

function getLngLatCorners(lngLatInDegr) {
  const minLng = Math.min(...lngLatInDegr.map(([lng, _]) => lng));
  const maxLng = Math.max(...lngLatInDegr.map(([lng, _]) => lng));
  const minLat = Math.min(...lngLatInDegr.map(([_, lat]) => lat));
  const maxLat = Math.max(...lngLatInDegr.map(([_, lat]) => lat));
  return {
    min: [minLng, minLat],
    max: [maxLng, maxLat],
  };
}
